'use client';

import classnames from 'classnames';
import { usePathname } from '@navigation';
import { notFound } from 'next/navigation';
import { PageHeader } from '@stadionhq-v3/base-ui';

import { FIXTURE_TYPE, FixtureType } from '@core/constants/fixtures.constant';
import { Route } from '@core/types/routes.types';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import templateConfig from '@templateConfig';
import subNavStyles from '@components/v3/atoms/page-navigation-segment/pageNavigationSegment.module.scss';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Team } from '@graphql/index';
import styles from './matchesHeader.module.scss';
import { MatchesHeaderParams } from './matchesHeader.types';

const MatchesHeader = ({
  params,
  classNames,
  labels,
  page,
  teamsSubNav,
}: MatchesHeaderParams) => {
  const classes = classnames([styles.matchesHeader, classNames]);

  const { team, competition } = params;

  const pathname = usePathname();

  const fixtureType: FixtureType | undefined = Object.values(FIXTURE_TYPE).find(
    (x: FixtureType) => pathname.includes(x)
  );

  if (!fixtureType) {
    return notFound();
  }

  const { fixturesLabel, resultsLabel, standingsLabel } = labels;

  const getHref = (
    route: Route,
    dropdownParams: {
      season?: string;
      competition?: string;
    } = {}
  ) =>
    generateFullPathname({
      locale: params.locale,
      route,
      params: { team: params.team, ...dropdownParams },
    });

  const heading =
    fixtureType === FIXTURE_TYPE.TABLE
      ? standingsLabel
      : fixtureType === FIXTURE_TYPE.RESULTS
        ? resultsLabel
        : fixturesLabel;

  let route: Route;

  switch (fixtureType) {
    case FIXTURE_TYPE.FIXTURES:
      route = templateConfig.navigation.routes.FIXTURES;
      break;

    case FIXTURE_TYPE.RESULTS:
      route = competition
        ? templateConfig.navigation.routes.RESULTS_COMPETITION
        : templateConfig.navigation.routes.RESULTS;
      break;

    case FIXTURE_TYPE.TABLE:
      route = competition
        ? templateConfig.navigation.routes.TABLE_COMPETITION
        : templateConfig.navigation.routes.TABLE;
      break;

    default:
      route = templateConfig.navigation.routes.FIXTURES;
      break;
  }

  return (
    <PageHeader
      classNames={[classes]}
      key={`fixture-page-header__${fixtureType}`}
      type="news-listing"
      title={page?.title || heading}
      summary={page.summary}
      label={page.label}
      sponsor={undefined}
      backgroundImage={
        page?.image ? <ContentfulImage {...page?.image} /> : undefined
      }
      subNav={teamsSubNav.map((teamsSubNavItem: CleanContentful<Team>) => ({
        title: teamsSubNavItem.name || '',
        url: generateFullPathname({
          locale: params.locale,
          route,
          params: {
            team: teamsSubNavItem.slug,
            ...(competition ? { competition } : {}),
          },
        }),
        selected: team === teamsSubNavItem.slug,
        classNames: [
          classnames(
            subNavStyles.pageNavSegment,
            subNavStyles.pageNavSegmentTokens,
            subNavStyles.onDark,
            {
              [`${subNavStyles.selected}`]: team === teamsSubNavItem.slug,
            }
          ),
        ],
      }))}
      segmentedControls={{
        tabs: [
          {
            id: fixturesLabel,
            text: fixturesLabel,
            url: getHref(templateConfig.navigation.routes.FIXTURES),
            useNext: true,
            scroll: false,
            selected: FIXTURE_TYPE.FIXTURES === fixtureType,
          },
          {
            id: resultsLabel,
            text: resultsLabel,
            url: competition
              ? getHref(templateConfig.navigation.routes.RESULTS_COMPETITION, {
                  competition,
                })
              : getHref(templateConfig.navigation.routes.RESULTS),
            useNext: true,
            scroll: false,
            selected: FIXTURE_TYPE.RESULTS === fixtureType,
          },
          {
            id: standingsLabel,
            text: standingsLabel,
            url: competition
              ? getHref(templateConfig.navigation.routes.TABLE_COMPETITION, {
                  competition,
                })
              : getHref(templateConfig.navigation.routes.TABLE),
            useNext: true,
            scroll: false,
            selected: FIXTURE_TYPE.TABLE === fixtureType,
          },
        ],
      }}
      onDark
      segmentedControlsOnDark
    />
  );
};

export default MatchesHeader;
